import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

const SUBJECTS = {
    NEET: ["Physics", "Chemistry", "Biology"],
    JEE: ["Physics", "Chemistry", "Mathematics"],
};

export default function GenerateQuestions() {
    const nav = useNavigate();
    const [exam, setExam] = useState("NEET");
    const [subjects, setSubjects] = useState(["Physics"]);
    const [difficulty, setDifficulty] = useState("mixed");
    const [count, setCount] = useState(15);
    const [topic, setTopic] = useState("");
    const [busy, setBusy] = useState(false);
    const [settings, setSettings] = useState(null);

    useEffect(() => { api.get("/settings/public").then((r) => {
        setSettings(r.data);
        setCount(Math.max(5, Math.min(50, Number(r.data.default_question_count) || 15)));
    }); }, []);

    useEffect(() => { setSubjects(SUBJECTS[exam].slice(0, 1)); }, [exam]);

    const toggleSubject = (s) => {
        setSubjects((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
    };

    const start = async () => {
        if (subjects.length === 0) { toast.error("Pick at least one subject"); return; }
        setBusy(true);
        try {
            const r = await api.post("/questions/generate", { exam, subjects, difficulty, count, topic: topic || null });
            toast.success(`${r.data.questions.length} questions ready`);
            nav(`/exam/${r.data.session_id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Generation failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
            <span className="overline text-primary">▮ Generate</span>
            <h1 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tighter">
                Configure your <span className="text-primary">practice set</span>
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
                Questions come from the community vector bank first, then LLM-generated on demand.
            </p>

            <div className="mt-10 grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border rounded-sm p-6 space-y-6">
                    <div>
                        <Label className="overline">Exam</Label>
                        <RadioGroup value={exam} onValueChange={setExam} className="mt-3 flex gap-2">
                            {["NEET", "JEE"].map((e) => (
                                <label key={e} className={`flex-1 border rounded-sm p-4 cursor-pointer btn-press transition-colors ${exam === e ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`} data-testid={`gen-exam-${e}`}>
                                    <RadioGroupItem value={e} className="sr-only" />
                                    <div className="font-heading font-black text-2xl">{e}</div>
                                    <div className="text-xs text-muted-foreground">{e === "NEET" ? "Medical entrance" : "Engineering entrance"}</div>
                                </label>
                            ))}
                        </RadioGroup>
                    </div>

                    <div>
                        <Label className="overline">Subjects</Label>
                        <div className="mt-3 grid grid-cols-3 gap-2">
                            {SUBJECTS[exam].map((s) => (
                                <label key={s} data-testid={`gen-subject-${s}`}
                                    className={`border rounded-sm p-3 flex items-center gap-2 cursor-pointer btn-press ${subjects.includes(s) ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}>
                                    <Checkbox checked={subjects.includes(s)} onCheckedChange={() => toggleSubject(s)} />
                                    <span className="text-sm font-medium">{s}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label className="overline">Topic (optional)</Label>
                        <Input data-testid="gen-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)}
                            placeholder="e.g. Rotational Motion, Coordination Compounds…"
                            className="rounded-sm mt-2 font-mono" />
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <Label className="overline">Difficulty</Label>
                            <Select value={difficulty} onValueChange={setDifficulty}>
                                <SelectTrigger data-testid="gen-difficulty-select" className="rounded-sm mt-2"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="easy">Easy</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="hard">Hard</SelectItem>
                                    <SelectItem value="mixed">Mixed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="overline">Questions: <span className="font-mono text-foreground">{count}</span></Label>
                            <Slider data-testid="gen-count-slider" value={[count]} min={5} max={50} step={5}
                                onValueChange={(v) => setCount(v[0])} className="mt-4" />
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="border border-border rounded-sm p-6">
                        <span className="overline text-muted-foreground">Summary</span>
                        <div className="mt-3 font-mono text-sm space-y-1">
                            <div>▮ Exam: <span className="text-foreground">{exam}</span></div>
                            <div>▮ Subjects: <span className="text-foreground">{subjects.join(", ") || "—"}</span></div>
                            <div>▮ Difficulty: <span className="text-foreground">{difficulty}</span></div>
                            <div>▮ Questions: <span className="text-foreground">{count}</span></div>
                            <div>▮ Duration: <span className="text-foreground">{settings?.default_exam_duration_minutes ?? 30} min</span></div>
                        </div>
                        <Button data-testid="gen-start-btn" onClick={start} disabled={busy || subjects.length === 0}
                            className="w-full mt-6 rounded-sm bg-primary hover:bg-primary/90 btn-press h-12">
                            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
                                : <><Sparkles className="w-4 h-4 mr-2" /> Generate & Start</>}
                        </Button>
                        <p className="text-[10px] text-muted-foreground mt-3">Generation may take 20-60s if the bank has no matching questions.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
